import {useState} from 'react';
import {X, Globe, Loader2, ChevronLeft, ChevronRight} from 'lucide-react';
import {QuizConfig} from '@/types/rewards';

interface RewardPreviewProps {
    title: string;
    description: string;
    buttonText: string;
    buttonColor?: string;
    buttonTextColor?: string;
    backgroundColor?: string;
    textColor?: string;
    showCloseButton?: boolean;
    showBranding?: boolean;
    language?: string;
    rewardType?: 'standard' | 'quiz';
    quizConfig?: QuizConfig;
}

/**
 * Rodape de identificacao da empresa, presente em todo offerwall.
 *
 * Espelha o que o rocket-script renderiza em runtime (src/brand.ts). O dominio
 * la e o do site que hospeda o script; aqui, como o preview roda no painel, vai
 * um placeholder so para ilustrar a posicao.
 */
const BrandFooter = ({textColor}: { textColor: string }) => (
    <div
        className="mt-5 pt-3.5 text-center leading-relaxed"
        style={{
            color: textColor,
            opacity: 0.6,
            fontSize: '0.68rem',
            borderTop: '1px solid rgba(128, 128, 128, 0.22)',
        }}
    >
        <p className="m-0">
            Rocket Group, by using this, you accept our{' '}
            <span className="underline">Privacy Policy</span> and{' '}
            <span className="underline">Terms</span>
        </p>
        <p className="mt-1.5 mb-0">
            &copy; {new Date().getFullYear()} seudominio.com. Published and operated by Rocket Group.
        </p>
    </div>
);

export const RewardPreview = ({
                                  title,
                                  description,
                                  buttonText,
                                  buttonColor = '#3b82f6',
                                  buttonTextColor = '#ffffff',
                                  backgroundColor = '#ffffff',
                                  textColor = '#1f2937',
                                  showCloseButton = true,
                                  showBranding = true,
                                  language = '',
                                  rewardType = 'standard',
                                  quizConfig,
                              }: RewardPreviewProps) => {

    // Quiz preview steps: 0..N-1 = quiz pages, N = final screen
    const [quizStep, setQuizStep] = useState(0);
    const pages = quizConfig?.pages || [];
    const totalPages = pages.length;
    const isQuiz = rewardType === 'quiz' && totalPages > 0;

    // Total steps = quiz pages + final screen
    const totalSteps = totalPages + 1;
    const isFinalStep = quizStep >= totalPages;

    // Standard reward preview
    if (!isQuiz) {
        return (
            <div className="space-y-2">
                {language && (
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <Globe className="h-4 w-4 text-gray-500"/>
                        <span className="text-sm font-medium text-gray-600">{language}</span>
                    </div>
                )}
                <div
                    className="relative w-full max-w-md mx-auto rounded-2xl shadow-2xl overflow-hidden transition-all duration-300"
                    style={{backgroundColor}}
                >
                    {showCloseButton && (
                        <button
                            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/10 hover:bg-black/20
                                 transition-colors flex items-center justify-center z-10"
                            aria-label="Fechar"
                        >
                            <X className="w-4 h-4" style={{color: textColor}}/>
                        </button>
                    )}

                    <div className="p-8">
                        <h3
                            className="text-2xl font-bold text-center mb-4"
                            style={{color: textColor}}
                        >
                            {title || 'Titulo do Offerwall'}
                        </h3>

                        {(description || !isQuiz) && (
                            <p
                                className="text-center mb-8 leading-relaxed"
                                style={{color: textColor, opacity: 0.8}}
                            >
                                {description || 'Descricao do offerwall aparecera aqui...'}
                            </p>
                        )}

                        <button
                            className="w-full py-4 px-6 rounded-xl font-semibold
                                 shadow-lg hover:shadow-xl transition-all duration-200
                                 transform hover:scale-105 active:scale-95"
                            style={{backgroundColor: buttonColor, color: buttonTextColor}}
                        >
                            {buttonText || 'Texto do Botao'}
                        </button>

                        {showBranding && <BrandFooter textColor={textColor}/>}
                    </div>
                </div>
            </div>
        );
    }

    // Quiz preview - step by step
    const currentPage = !isFinalStep ? pages[quizStep] : null;

    const goNext = () => {
        if (quizStep < totalSteps - 1) setQuizStep(quizStep + 1);
        else setQuizStep(0);
    };

    const goPrev = () => {
        if (quizStep > 0) setQuizStep(quizStep - 1);
    };

    return (
        <div className="space-y-2">
            {/* Navigation controls */}
            <div className="flex items-center justify-between mb-1">
                <button
                    onClick={goPrev}
                    disabled={quizStep === 0}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                    <ChevronLeft className="h-3 w-3"/>
                    Anterior
                </button>
                <span className="text-xs text-gray-400">
                    {quizStep + 1} / {totalSteps}
                    {isFinalStep ? ' (Tela Final)' : ''}
                </span>
                <button
                    onClick={goNext}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                >
                    {quizStep < totalSteps - 1 ? 'Proximo' : 'Reiniciar'}
                    <ChevronRight className="h-3 w-3"/>
                </button>
            </div>

            <div
                className="relative w-full max-w-md mx-auto rounded-2xl shadow-2xl overflow-hidden transition-all duration-300"
                style={{backgroundColor}}
            >
                {/* Mesmo X do ramo standard: o toggle "Botao de Fechar" vale
                    para os dois tipos, e o preview precisa refletir isso. */}
                {showCloseButton && (
                    <button
                        className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/10 hover:bg-black/20
                             transition-colors flex items-center justify-center z-10"
                        aria-label="Fechar"
                    >
                        <X className="w-4 h-4" style={{color: textColor}}/>
                    </button>
                )}

                <div className="p-8">
                    {/* Question page */}
                    {currentPage?.type === 'question' && (
                        <div className="space-y-5">
                            <h3
                                className="text-xl font-semibold text-center leading-snug"
                                style={{color: textColor}}
                            >
                                {currentPage.question_text || `Pergunta ${quizStep + 1}`}
                            </h3>

                            <div className="space-y-3">
                                {(currentPage.options || []).map((opt) => (
                                    <button
                                        key={opt.id}
                                        className="w-full py-4 px-6 rounded-xl font-semibold text-center
                                             shadow-md hover:shadow-lg transition-all duration-200
                                             transform hover:scale-[1.02] active:scale-[0.98]"
                                        style={{backgroundColor: buttonColor, color: buttonTextColor}}
                                        onClick={goNext}
                                    >
                                        {opt.text || 'Opcao'}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Image Grid page */}
                    {currentPage?.type === 'image_grid' && (
                        <div className="space-y-3">
                            {currentPage.subtitle && (
                                <p className="text-xs font-medium text-center" style={{color: buttonColor}}>
                                    {currentPage.subtitle}
                                </p>
                            )}
                            <h3
                                className="text-lg font-semibold text-center leading-snug"
                                style={{color: textColor}}
                            >
                                {currentPage.question_text || 'Pergunta'}
                            </h3>
                            <div className="grid grid-cols-2 gap-2">
                                {(currentPage.options || []).map((opt) => (
                                    <button
                                        key={opt.id}
                                        className="rounded-xl overflow-hidden border-2 border-gray-200
                                             hover:border-current transition-all duration-200
                                             transform hover:scale-[1.02] active:scale-[0.98]"
                                        style={{borderColor: 'transparent'}}
                                        onClick={goNext}
                                    >
                                        {opt.image_url ? (
                                            <img
                                                src={opt.image_url}
                                                alt={opt.text}
                                                className="w-full h-20 object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-20 bg-gray-100 flex items-center justify-center">
                                                <span className="text-gray-400 text-xs">Sem imagem</span>
                                            </div>
                                        )}
                                        <div
                                            className="py-2 px-1 text-xs font-semibold text-center"
                                            style={{backgroundColor: buttonColor, color: buttonTextColor}}
                                        >
                                            {opt.text || 'Label'}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Loader page */}
                    {currentPage?.type === 'loader' && (
                        <div className="flex flex-col items-center gap-5 py-6">
                            {/* Loader Animation */}
                            <div className="flex items-center justify-center h-[60px]">
                                {(!currentPage.loader_animation || currentPage.loader_animation === 'spinner') && (
                                    <Loader2
                                        className="h-12 w-12 animate-spin"
                                        style={{color: buttonColor}}
                                    />
                                )}
                                {currentPage.loader_animation === 'dots' && (
                                    <div className="flex gap-2 items-center">
                                        {[0, 1, 2].map((i) => (
                                            <div
                                                key={i}
                                                className="w-3.5 h-3.5 rounded-full animate-bounce"
                                                style={{
                                                    backgroundColor: buttonColor,
                                                    animationDelay: `${i * 0.15}s`,
                                                    animationDuration: '1.4s',
                                                }}
                                            />
                                        ))}
                                    </div>
                                )}
                                {currentPage.loader_animation === 'pulse' && (
                                    <div
                                        className="w-12 h-12 rounded-full animate-pulse"
                                        style={{backgroundColor: buttonColor}}
                                    />
                                )}
                                {currentPage.loader_animation === 'bars' && (
                                    <div className="flex gap-1 items-end h-10">
                                        {[0, 1, 2, 3].map((i) => (
                                            <div
                                                key={i}
                                                className="w-2 rounded animate-pulse"
                                                style={{
                                                    backgroundColor: buttonColor,
                                                    height: '100%',
                                                    animationDelay: `${i * 0.15}s`,
                                                    animationDuration: '1.2s',
                                                }}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                            <p
                                className="text-center text-base animate-pulse"
                                style={{color: textColor, opacity: 0.8}}
                            >
                                {currentPage.loader_text || 'Carregando...'}
                            </p>
                            <span className="text-xs opacity-40" style={{color: textColor}}>
                                {currentPage.duration || 3}s
                                {currentPage.wait_for_cascade && ' + cascade'}
                            </span>
                        </div>
                    )}

                    {/* Push notification page */}
                    {currentPage?.type === 'push_notification' && (
                        <div className="space-y-4 text-center">
                            <h3
                                className="text-xl font-bold leading-snug"
                                style={{color: textColor}}
                            >
                                {currentPage.title || 'Titulo do push'}
                            </h3>
                            {currentPage.description && (
                                <p
                                    className="text-sm leading-relaxed"
                                    style={{color: textColor, opacity: 0.8}}
                                >
                                    {currentPage.description}
                                </p>
                            )}
                            <button
                                className="w-full py-4 px-6 rounded-xl font-semibold text-center
                                     shadow-md hover:shadow-lg transition-all duration-200
                                     transform hover:scale-[1.02] active:scale-[0.98]"
                                style={{backgroundColor: buttonColor, color: buttonTextColor}}
                                onClick={goNext}
                            >
                                {currentPage.button_text || 'Permitir notificacoes'}
                            </button>
                        </div>
                    )}

                    {/* Final screen */}
                    {isFinalStep && (
                        <div className="space-y-4">
                            <h3
                                className="text-xl font-semibold text-center leading-snug"
                                style={{color: textColor}}
                            >
                                {title || 'Titulo da Tela Final'}
                            </h3>

                            <button
                                className="w-full py-4 px-6 rounded-xl font-semibold text-center
                                     shadow-md hover:shadow-lg transition-all duration-200
                                     transform hover:scale-[1.02] active:scale-[0.98]"
                                style={{backgroundColor: buttonColor, color: buttonTextColor}}
                            >
                                {buttonText || 'Texto do Botao'}
                            </button>

                            {description && (
                                <p
                                    className="text-center text-sm"
                                    style={{color: textColor, opacity: 0.6}}
                                >
                                    {description}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Progress dots */}
                    <div className="flex justify-center gap-1.5 mt-6">
                        {Array.from({length: totalSteps}).map((_, i) => (
                            <div
                                key={i}
                                className="w-2 h-2 rounded-full transition-all duration-300 cursor-pointer"
                                style={{
                                    backgroundColor: i <= quizStep ? buttonColor : `${textColor}20`,
                                    transform: i === quizStep ? 'scale(1.3)' : 'scale(1)',
                                }}
                                onClick={() => setQuizStep(i)}
                            />
                        ))}
                    </div>

                    <BrandFooter textColor={textColor}/>
                </div>
            </div>
        </div>
    );
};
